import {CASE_STUDIES} from "../../data/caseStudies";
import {useState} from "react";
import {FiArrowUpRight, FiPlus} from "react-icons/fi";
import {STOCK_DRAWER} from "../../data/stockDrawer";
import {ENGINEERING_EVIDENCE_EXPLORER} from "../../data/engineeringEvidenceExplorer";
import {ROAD_TEST} from "../../data/roadTest";
import {MATCHNOTE} from "../../data/matchnote";

const PROJECTS = [
    {
        ...STOCK_DRAWER,
        id: "stock-drawer",
        caseStudy: CASE_STUDIES["stock-drawer"],
        deck: "A different way to explore market history.",
        description: "Sketch a market shape, find similar historical movements, and replay the original prices beneath your drawing.",
        detail: "I built the interactive canvas, background pattern matching, and replayable chart as one focused research workflow.",
        technologies: ["Nuxt", "Vue", "TypeScript", "Vitest + Playwright"],
        duration: "1:17",
        captionsDefault: true,
        note: "Historical shapes, not predictions.",
    },
    {
        ...ENGINEERING_EVIDENCE_EXPLORER,
        id: "engineering-evidence-explorer",
        caseStudy: CASE_STUDIES["engineering-evidence-explorer"],
        deck: "Follow a recommendation back to the work behind it.",
        description: "Ask about an engineering team, inspect the MCP tool calls, and trace each answer to supporting pull requests and reviews.",
        detail: "I built a shared TypeScript evidence engine with visible tool traces and explicit limits when the evidence runs out.",
        technologies: ["Nuxt", "Vue", "TypeScript", "MCP"],
        duration: "1:20",
        // This film already has captions burned into the picture.
        captionsDefault: false,
        note: "Fictional team and records. Genuine MCP tool calls.",
    },
    {
        ...ROAD_TEST,
        id: "road-test",
        caseStudy: CASE_STUDIES["road-test"],
        deck: "Can an AI finish the job when something goes wrong?",
        description: "Give AI models the same rental-car request, introduce a failure, and replay whether each model recovers successfully.",
        detail: "I paired an independent Python evaluator with a Nuxt viewer for tool calls, recovery attempts, timing, and estimated cost.",
        technologies: ["Nuxt", "TypeScript", "Python", "Agent evaluation"],
        duration: "1:50",
        // This film already has captions burned into the picture.
        captionsDefault: false,
        note: "Real model recordings. Fictional rental-car environment.",
    },
    {
        ...MATCHNOTE,
        id: "matchnote",
        caseStudy: CASE_STUDIES["matchnote"],
        deck: "Catch the difference before it gets paid.",
        description: "Review fictional invoices against purchase orders and receipts, then trace price, quantity, and duplicate-record exceptions to the underlying evidence.",
        detail: "I built the matching rules and a focused review flow where a person can hold an exception or mark a clean invoice ready for payment.",
        technologies: ["JavaScript", "Node.js", "Rule-based matching", "Automated tests"],
        duration: "0:30",
        // The film has captions burned into the picture.
        captionsDefault: false,
        note: "Fictional records. Decisions reset on reload.",
    },
];

const ProjectShowcase = ({project}: {project: typeof PROJECTS[number]}) => {
    const [videoError, setVideoError] = useState(false);
    const study = project.caseStudy;
    const descriptionId = `${project.id}-description`;
    return (
        <article id={project.id} className="featured-project" aria-labelledby={`${project.id}-title`}>
            <div className="project-media">
                <video id={`${project.id}-walkthrough`} controls playsInline preload="none" poster={project.poster} aria-label={`${project.title} product walkthrough`} aria-describedby={descriptionId} onError={() => setVideoError(true)}>
                    <source src={project.video} type="video/mp4" />
                    <track kind="captions" src={project.captions} srcLang="en" label="English" default={project.captionsDefault} />
                    Your browser does not support embedded video. <a href={project.video}>Open the walkthrough</a>.
                </video>
                <div className="media-caption"><span>Product walkthrough</span><span>{project.duration} · English captions</span></div>
                {videoError && <p className="media-error" role="alert">The video couldn’t load. <a href={project.video}>Open the video directly</a> or refresh to try again.</p>}
            </div>
            <div className="featured-copy">
                <div className="project-title-row"><h3 id={`${project.id}-title`}>{project.title}</h3><span className="project-status">{project.liveUrl ? "Live" : project.repositoryUrl ? "Demo + source" : "Video preview"}</span></div>
                <p className="project-deck">{project.deck}</p>
                <p id={descriptionId} className="project-description">{project.description}</p>
                <p className="project-detail">{project.detail}</p>
                <ul className="tech-list" aria-label={`${project.title} technologies`}>{project.technologies.map(tech => <li key={tech}>{tech}</li>)}</ul>
                <div className="project-launch">
                    {project.liveUrl ? <a className="text-link" href={project.liveUrl} target="_blank" rel="noopener noreferrer" aria-label={`Try ${project.title} live`}>Try it live <FiArrowUpRight aria-hidden="true" /></a> : <a className="text-link" href={`#${project.id}-walkthrough`} aria-label={`Watch ${project.title} walkthrough`}>Watch walkthrough <FiArrowUpRight aria-hidden="true" /></a>}
                    {project.repositoryUrl && <a className="text-link repository-link" href={project.repositoryUrl} target="_blank" rel="noopener noreferrer" aria-label={`View ${project.title} source`}>View source <FiArrowUpRight aria-hidden="true" /></a>}
                    <span>{project.note}</span>
                </div>
            </div>
            <details className="project-case-study" id={`${project.id}-case-study`}>
                <summary aria-label={`Read ${project.title} engineering case study`}>
                    <span><span className="case-index" aria-hidden="true">{study.number}</span> Engineering case study</span>
                    <span className="case-summary-action"><span className="case-read">Read the build notes</span><span className="case-close">Close notes</span><FiPlus aria-hidden="true" /></span>
                </summary>
                <div className="case-body">
                    <p className="case-file" aria-hidden="true">projects / {project.id} / case-study.md</p>
                    <h4>{study.title}</h4>
                    <div className="case-intro-grid">
                        <div><h5>The problem</h5><p>{study.problem}</p></div>
                        <div><h5>My role</h5><p>{study.role}</p></div>
                    </div>
                    <div className="case-architecture">
                        <h5>Architecture</h5>
                        <ol aria-label={`${project.title} architecture flow`}>{study.architecture.map((step, index) => <li key={step}><span className="flow-number" aria-hidden="true">0{index + 1}</span>{step}</li>)}</ol>
                        <p>{study.architectureDetail}</p>
                    </div>
                    <div className="case-intro-grid">
                        <div><h5>The key trade-off</h5><p>{study.tradeoff}</p></div>
                        <div><h5>Engineering takeaway</h5><p>{study.takeaway}</p></div>
                    </div>
                    <div className="case-evidence"><p>{study.evidence}</p>{study.sourceUrl && <a className="text-link" href={study.sourceUrl} target="_blank" rel="noopener noreferrer">{study.sourceLabel}<FiArrowUpRight aria-hidden="true" /></a>}</div>
                </div>
            </details>
        </article>
    );
};

export const ProjectsSection = () => (
    <section id="projects" className="section shell work-section" aria-labelledby="work-title">
        <div className="section-heading">
            <h2 id="work-title">Selected work<span className="accent-dot">.</span></h2>
            <p>Explore market patterns, trace engineering evidence, test AI agents, and review invoice discrepancies. Four projects, from the interface to the systems underneath.</p>
        </div>
        {PROJECTS.map(project => <ProjectShowcase key={project.id} project={project} />)}
    </section>
);
